import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import * as XLSX from 'xlsx';
import debounce from 'lodash/debounce';

import ScheduleMarkSettleDownloadReportComponent from "./component";

const ScheduleMarkSettleReportContainer = (props) => {
  const { date } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMarkSettleDialog, setShowMarkSettleDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showMismatchDialog, setShowMismatchDialog] = useState(false);
  const [mismatchData, setMismatchData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [checkResponse, setCheckResponse] = useState(null);
  const [isCheckingFile, setIsCheckingFile] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [uploadedFileSize, setUploadedFileSize] = useState(null);
  const [mismatchType, setMismatchType] = useState(null);
  const [mismatchDetails, setMismatchDetails] = useState([]);
  const [apiErrorMessage, setApiErrorMessage] = useState(null);
  const [showSimpleSuccessPopup, setShowSimpleSuccessPopup] = useState(false);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [fileData, setFileData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const formatDateForPayload = (dateString) => {
    if (!dateString) return null;
    
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    
    try {
      let dateObj;
      if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
        }
      } else if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
          } else {
            dateObj = new Date(parts[2], parts[1] - 1, parts[0]);
          }
        }
      } else {
        dateObj = new Date(dateString);
      }
      
      if (!isNaN(dateObj.getTime())) {
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      console.error("Date parsing error:", e);
    }
    
    return dateString;
  };

  const getFileSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) {
      return `${mb.toFixed(1)}MB`;
    }
    return `${kb.toFixed(1)}KB`;
  };

  useEffect(() => {
    fetchSchedulePayoutData();
  }, [date]);

  const fetchSchedulePayoutData = debounce(() => {
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v3/acc/payouts/schedule?date=${date}`,
      'GET',
      undefined,
      (response) => {
        if (response) {
          setReportData(response || {});
        } else {
          setReportData({});
        }
        setLoading(false);
      },
      (err) => {
        console.error("API Error:", err);
        setError(err.message || "Failed to load payout data");
        toastr.error("Error", "Failed to load payout data");
        setLoading(false);
      }
    );
  }, 300);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('₹', '₹ ');
  };

  const handleDownloadExcel = () => {
    try {
      const dataToExport = reportData.cohortScheduleResponseDto?.map(item => ({
        'Partner ID': item.franchiseeId || 'N/A',
        'Partner Name': item.payee || 'N/A',
        'Cohort Id': item.cohortId || 'N/A',
        'Status': item.status || 'Generated',
        'Deductions': item.deductions,
        'Payout': item.payout || 0,
        'saas': item.saas || 0,
        'raas': item.raas || 0,
        'pgFee': item.pgFee || 0,
        'rental': item.rental || 0,
        'miscCredit': item.miscCredit || 0,
        'miscDebit': item.miscDebit || 0,
        'Transfer Text': item.transferText || 'N/A'
      })) || [];

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Payouts Report");
      XLSX.writeFile(wb, `payout-report-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toastr.error('Error', 'Failed to download report');
    }
  };

  const handleMarkSettleClick = () => {
    setShowMarkSettleDialog(true);
    setSelectedFile(null);
    setCheckResponse(null);
    setMismatchData(null);
    setCheckError(null);
    setUploadError(null);
    setIsCheckingFile(false);
    setIsUploading(false);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setMismatchType(null);
    setMismatchDetails([]);
    setApiErrorMessage(null);
    setFileHeaders([]);
    setFileData([]);
  };

  const resetFileSelection = () => {
    setSelectedFile(null);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setCheckError(null);
    setUploadError(null);
    setFileHeaders([]);
    setFileData([]);
  };

  const handleRetryUpload = () => {
    setCheckError(null);
    setUploadError(null);
    setSelectedFile(null);
    setCheckResponse(null);
    setMismatchData(null);
    setIsCheckingFile(false);
    setIsUploading(false);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setMismatchType(null);
    setMismatchDetails([]);
    setApiErrorMessage(null);
    setFileHeaders([]);
    setFileData([]);
    setShowMarkSettleDialog(true);
  };

  const formatErrorMessage = (err) => {
    const errorMsg = err?.message || err?.error || "";
    
    if (errorMsg.includes("Something went wrong") || errorMsg.includes("something went wrong")) {
      return "File Corrupted, Try Again";
    }
    
    return errorMsg || "Failed to process request";
  };

  const readFileHeaders = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          
          if (jsonData.length > 0) {
            const headers = Object.keys(jsonData[0]);
            resolve({ headers, data: jsonData });
          } else {
            resolve({ headers: [], data: [] });
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // ✅ CHECK API - Only checks, no auto upload
  const handleFileCheck = async (file) => {
    if (!file) return;
    
    setSelectedFile(file);
    setUploadedFileName(file.name);
    setUploadedFileSize(getFileSize(file.size));
    setIsCheckingFile(true);
    setCheckError(null);
    setUploadError(null);
    setApiErrorMessage(null);
    setMismatchType(null);
    setShowMismatchDialog(false);
    setShowSuccessDialog(false);
    
    try {
      const { headers, data } = await readFileHeaders(file);
      setFileHeaders(headers);
      setFileData(data);
      
      console.log("✅ Dynamic Headers Found:", headers);
      console.log("✅ Total Records:", data.length);
    } catch (error) {
      console.error("Error reading file:", error);
    }
    
    const formData = new FormData();
    formData.append('file', file);
    const formattedDate = formatDateForPayload(date);
    formData.append('date', formattedDate);
    
    props.getDataFromAPI(
      `/partner/check/settlement`,
      "POST",
      formData,
      (response) => {
        console.log("✅ Check API Response:", response);
        setIsCheckingFile(false);
        setCheckResponse(response);
        
        if (response) {
          const totalCount = response.totalCount || 0;
          const totalMatchCount = response.totalMatchCount || 0;
          const mismatchCount = response.mismatchCount || 0;
          const mismatchDetailsList = response.mismatchDetails || [];
          
          const isFullMatch = totalCount === totalMatchCount && mismatchCount === 0;
          
          if (isFullMatch) {
            // ✅ FULL MATCH - Show Success Dialog with Upload button
            setShowMarkSettleDialog(false);
            setMismatchData({
              totalCount: totalCount,
              matchedCount: totalMatchCount,
              mismatchCount: 0
            });
            setMismatchType('full_match');
            setMismatchDetails([]);
            
            setTimeout(() => {
              setShowSuccessDialog(true);
              console.log("✅ Full Match Dialog Opened");
            }, 300);
          } 
          else {
            // ✅ PARTIAL MATCH - Show Mismatch Dialog with Upload button
            setShowMarkSettleDialog(false);
            setMismatchType('partial');
            setMismatchData({
              totalCount: totalCount,
              matchedCount: totalMatchCount,
              mismatchCount: mismatchCount
            });
            setMismatchDetails(mismatchDetailsList);
            
            setTimeout(() => {
              setShowMismatchDialog(true);
              console.log("✅ Partial Match Dialog Opened");
            }, 300);
          }
        }
      },
      (err) => {
        console.error("❌ Check API Error:", err);
        setIsCheckingFile(false);
        const formattedError = formatErrorMessage(err);
        setCheckError(formattedError);
        setApiErrorMessage(formattedError);
      }
    );
  };

  // ✅ UPLOAD API - Called only when user clicks Upload button
  const handleUploadFile = () => {
    if (!selectedFile) {
      setUploadError("No file selected");
      toastr.error("Error", "No file selected");
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    const formattedDate = formatDateForPayload(date);
    formData.append('date', formattedDate);
    
    console.log("📤 Uploading file:", selectedFile.name);
    console.log("📅 Formatted date:", formattedDate);
    
    props.getDataFromAPI(
      `/partner/upload-settlement`,
      "POST",
      formData,
      (response) => {
        console.log("✅ Upload API Success:", response);
        setIsUploading(false);
        
        // Close both dialogs
        setShowSuccessDialog(false);
        setShowMismatchDialog(false);
        setSelectedFile(null);
        setCheckResponse(null);
        setFileHeaders([]);
        setFileData([]);
        setMismatchType(null);
        setMismatchData(null);
        setMismatchDetails([]);
        
        // Show success popup
        setTimeout(() => {
          setShowSimpleSuccessPopup(true);
         
        }, 300);
        
        toastr.success("Success", "Settlement file uploaded successfully!");
      },
      (err) => {
        console.error("❌ Upload API Error:", err);
        setIsUploading(false);
        const formattedError = formatErrorMessage(err);
        setUploadError(formattedError);
        toastr.error("Error", formattedError);
      }
    );
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setMismatchData(null);
    setMismatchType(null);
    setFileHeaders([]);
    setFileData([]);
    setMismatchDetails([]);
  
    navigate("/home/configuration/payoutpartners");
  };

  const handleSimpleSuccessClose = () => {
    setShowSimpleSuccessPopup(false);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setMismatchData(null);
    setMismatchType(null);
    setFileHeaders([]);
    setFileData([]);
    setMismatchDetails([]);
    
    navigate("/home/configuration/payoutpartners");
  };

  const handleMismatchCancel = () => {
    setShowMismatchDialog(false);
    setSelectedFile(null);
    setCheckResponse(null);
    setCheckError(null);
    setUploadError(null);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setMismatchType(null);
    setMismatchDetails([]);
    setApiErrorMessage(null);
    setFileHeaders([]);
    setFileData([]);
    setIsUploading(false);
  };

  const handleMismatchUpload = () => {
    console.log("🔵 Upload button clicked in mismatch dialog");
    handleUploadFile();
  };

  const handleFullMatchUpload = () => {
    console.log("🔵 Upload button clicked in full match dialog");
    handleUploadFile();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const onFileSelect = (file) => {
    handleFileCheck(file);
  };

  return (
    <ScheduleMarkSettleDownloadReportComponent
      loading={loading}
      error={error}
      reportData={reportData}
      formatCurrency={formatCurrency}
      onDownloadExcel={handleDownloadExcel}
      onGoBack={handleGoBack}
      onMarkSettle={handleMarkSettleClick}
      showMarkSettleDialog={showMarkSettleDialog}
      setShowMarkSettleDialog={setShowMarkSettleDialog}
      showSuccessDialog={showSuccessDialog}
      onSuccessClose={handleSuccessClose}
      onFullMatchUpload={handleFullMatchUpload}
      showMismatchDialog={showMismatchDialog}
      mismatchData={mismatchData}
      onMismatchCancel={handleMismatchCancel}
      onMismatchUpload={handleMismatchUpload}
      onFileSelect={onFileSelect}
      checkError={checkError}
      isCheckingFile={isCheckingFile}
      uploadError={uploadError}
      setUploadError={setUploadError}
      onRetryUpload={handleRetryUpload}
      uploadedFileName={uploadedFileName}
      uploadedFileSize={uploadedFileSize}
      mismatchType={mismatchType}
      mismatchDetails={mismatchDetails}
      showSimpleSuccessPopup={showSimpleSuccessPopup}
      onSimpleSuccessClose={handleSimpleSuccessClose}
      onResetFileSelection={resetFileSelection}
      fileHeaders={fileHeaders}
      fileData={fileData}
      isUploading={isUploading}
    />
  );
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {
  getDataFromAPI,
})(ScheduleMarkSettleReportContainer);


// import React, { useState, useEffect } from "react";
// import { connect } from "react-redux";
// import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
// import { useNavigate, useLocation, useParams } from "react-router-dom";
// import {toastr} from "react-redux-toastr";
// import * as XLSX from 'xlsx';
// import debounce from 'lodash/debounce';
// import { format } from 'date-fns';

// import ScheduleMarkSettleDownloadReportComponent from "./component";

// const ScheduleMarkSettleReportContainer = (props) => {
//   const { date } = useParams();
//   const navigate = useNavigate();
//   const [reportData, setReportData] = useState({});
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedPayoutIds, setSelectedPayoutIds] = useState([]);
//   const [selectAll, setSelectAll] = useState(false);
//   const [showMarkSettleDialog, setShowMarkSettleDialog] = useState(false);
//   const [showSuccessDialog, setShowSuccessDialog] = useState(false);
//   const [selectedDate, setSelectedDate] = useState(null);

//   useEffect(() => {
//     fetchSchedulePayoutData();
//   }, [date]);

//   const fetchSchedulePayoutData = debounce(() => {
//     setLoading(true);
//     props.getDataFromAPI(
//       `/partner/api/v3/acc/payouts/schedule?date=${date}`,
//       'GET',
//       undefined,
//       (response) => {
//         if (response) {
//           setReportData(response|| {});
//         } else {
//           setReportData({});
//         }
//         setLoading(false);
//       },
//       (err) => {
//         console.error("API Error:", err);
//         setError(err.message || "Failed to load payout data");
//         toastr.error("Error", "Failed to load payout data");
//         setLoading(false);
//       }
//     );
//   }, 300);

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0
//     }).format(amount).replace('₹', '₹ ');
//   };

//   const handleDownloadExcel = () => {
//     try {
//       const dataToExport = reportData.cohortScheduleResponseDto?.map(item => ({
//         'Partner ID': item.franchiseeId || 'N/A',
//         'Partner Name': item.payee || 'N/A',
//         'Cohort Id':item.cohortId || 'N/A',
//         'Status': item.status || 'Generated',
//         'Deductions': item.deductions,
//         'Payout': item.payout || 0,
//         'saas': item.saas || 0,
//         'raas':item.raas || 0,
//         'pgFee': item.pgFee || 0,
//         'rental':item.rental || 0,
//         'miscCredit': item.miscCredit || 0,
//         'miscDebit': item.miscDebit || 0,
//         'Transfer Text': item.transferText || 'N/A'
//       })) || [];
      
//       const ws = XLSX.utils.json_to_sheet(dataToExport);
//       const wb = XLSX.utils.book_new();
//       XLSX.utils.book_append_sheet(wb, ws, "Payouts Report");
//       XLSX.writeFile(wb, `payout-report-${new Date().toISOString().split('T')[0]}.xlsx`);
//     } catch (error) {
//       console.error('Error exporting to Excel:', error);
//       toastr.error('Error', 'Failed to download report');
//     }
//   };

//   const handleMarkSettleClick = (payoutIds) => {
//     setSelectedPayoutIds(payoutIds);
//     setShowMarkSettleDialog(true);
//   };

//   const handleMarkSettledConfirm = () => {
//     if (!selectedDate) {
//       toastr.error('Error', 'Please select a settlement date');
//       return;
//     }

//     if (selectedPayoutIds.length === 0) {
//       toastr.error('Error', 'Please select at least one payout to mark as settled');
//       return;
//     }

//     const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    
//     props.getDataFromAPI(
//       `/partner/api/v3/acc/payouts/mark-settle/?date=${date}`,
//       "PATCH",
//       { 
//         payoutIds: selectedPayoutIds,
//         settleDate: formattedDate  
//       },
//       (response) => {
//         setShowMarkSettleDialog(false);
//         setShowSuccessDialog(true);
//         toastr.success('Success', `${selectedPayoutIds.length} payout(s) marked as settled successfully`);
       
//       },
//       (err) => {
//         console.error("API Error:", err);
//         toastr.error("Error", err.message || "Failed to mark payout as settled");
//       }
//     );
//   };

//   // const handleSuccessClose = () => {
//   //   setShowSuccessDialog(false);
//   //   setSelectedPayoutIds([]);
//   // };
//   const handleSuccessClose = (date) => {
//     setShowSuccessDialog(false);
//     navigate(-1);
//   };
//   const handleGoBack = () => {
//     navigate(-1);
//   };

//   return (
//     <ScheduleMarkSettleDownloadReportComponent
//       loading={loading}
//       error={error}
//       reportData={reportData}
//       formatCurrency={formatCurrency}
//       onDownloadExcel={handleDownloadExcel}
//       onGoBack={handleGoBack}
//       onMarkSettle={handleMarkSettleClick}
//       showMarkSettleDialog={showMarkSettleDialog}
//       setShowMarkSettleDialog={setShowMarkSettleDialog}
//       selectedDate={selectedDate}
//       setSelectedDate={setSelectedDate}
//       onMarkSettledConfirm={handleMarkSettledConfirm}
//       showSuccessDialog={showSuccessDialog}
//       onSuccessClose={handleSuccessClose}
//       selectedPayoutIds={selectedPayoutIds}
//       setSelectedPayoutIds={setSelectedPayoutIds}
//       selectAll={selectAll}
//       setSelectAll={setSelectAll}
//       payoutData={reportData}
//     />
//   );
// };

// const mapStateToProps = (state) => ({
//   // Add any state props you need
// });

// export default connect(mapStateToProps, {
//   getDataFromAPI,
// })(ScheduleMarkSettleReportContainer);